/*
  Main entry point of the application. This script initializes the React application
  and renders the main App component into the DOM.
*/

// Core Library Imports
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';


// Component Imports
import App from './init/App';
import { GlobalProvider } from './init/GlobalContext';

// Utility Imports
import './init/i18n';

// Style Imports
import './assets/scss/styles.scss';


// Create a root container where the app will be mounted
const container = document.getElementById('root');
const root = createRoot(container);

// Simple Error Boundary fallback component
const ErrorFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
    textAlign: 'center'
  }}>
    <div>
      <h1>Something went wrong</h1>
      <p>Please try refreshing the page</p>
      <button onClick={() => window.location.reload()} style={{
        padding: '10px 20px',
        marginTop: '20px',
        cursor: 'pointer'
      }}>
        Refresh Page
      </button>
    </div>
  </div>
);

// Conditionally wrap with Sentry ErrorBoundary only if Sentry is initialized
const ErrorBoundaryWrapper = ({ children }) => {
  try {
    // Check if Sentry is available and initialized
    if (Sentry && Sentry.ErrorBoundary) {
      return (
        <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
          {children}
        </Sentry.ErrorBoundary>
      );
    }
  } catch (e) {
    console.warn('Sentry ErrorBoundary not available');
  }
  // If Sentry is not available, just return children
  return children;
};

// Wrap the application inside the HelmetProvider for managing changes to the
// document head and BrowserRouter for routing capabilities
root.render(
  <GlobalProvider>
    <HelmetProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </HelmetProvider>
  </GlobalProvider>
);